/**
 *
 * PluginSelectionModal
 *
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { Spinner } from '@pearsontechnology/aero-components';
import { findPropertiesByType } from '@pearsontechnology/aero-plugin-schema';

import { commandAuthorPluginList } from 'containers/ConnectionManager/actions';
import { selectPluginTypeList } from 'domains/author/selectors';
import enhanceWithThemes from 'hocs/enhanceWithThemes';
import templateServiceShape from 'services/types/templateService';
import pluginServiceShape from 'services/types/pluginService';
import pluginTypes from 'constants/pluginTypes';

import {
  PluginCollection,
  FullHeightWrapper,
  LoadingWrapper,
  PluginSelection2ndStepWrapper,
  CenteringWrapper,
} from './styles';
import themes from './themes';
import PluginSelectionHeader from './components/PluginSelectionHeader';
import PluginSelectionHeader2ndStep from './components/PluginSelectionHeader2ndStep';
import CategoryList from './components/CategoryList';

import getConfig, { getConfig2ndStep } from './configText';
import * as constants from './constants';

import PluginSelectionView from './views/PluginSelection';
import TemplateSelectionView from './views/TemplateSelection';
import PluginSelection2ndStep from './views/PluginSelection2ndStep';
import enhanceWithServices from '../../hocs/enhanceWithServices';
import pathwayTypesConstants from 'constants/pathwayTypes';
import { pluginsSkipChoosePathway } from './pluginsSkipChoosePathway';
class PluginSelectionModal extends React.Component {
  state = {
    selectedCategory: undefined,
    name: '',
    description: '',
    selectedView: constants.TEMPLATE,
    selectedActivity: undefined,
    isLoading: false,
  };

  componentDidMount() {
    this.props.getPlugins();
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  setSelectedCategory = (category, type) => () => this.setState({
    selectedCategory: category,
    selectedView: type || constants.PLUGIN,
  });

  // eslint-disable-next-line react/sort-comp
  goTo2ndStepPathways = (pluginId, version, pluginType, pluginSchema) => this.setState({
    selectedView: constants.PLUGIN_2ND_STEP_PATHWAYS,
    selectedActivity: { pluginId, version, pluginType, pluginSchema },
  });

  goBackTo1stStep = () => this.setState({
    selectedView: constants.PLUGIN,
    selectedActivity: undefined,
  });

  setLoadingStatus = (value) => {
    if (this.mounted) {
      this.setState({
        isLoading: value || false,
      });
    }
  };

  getTemplateNavItems = (templateCategories) => templateCategories.map((category) => ({
    text: category,
    key: category,
    type: constants.TEMPLATE,
  }));

  getPluginCategory = (plugins) => plugins && plugins.length > 0 ? ({
    text: 'Blank',
    key: 'plugins',
    type: constants.PLUGIN,
  }) : [];

  updateInputField = (event) => this.setState({ [event.target.name]: event.target.value });

  updateName = (updatedName) => this.setState({ name: updatedName });

  updateDescription = (updatedDescription) => this.setState({ description: updatedDescription });

  handleTemplateSelect = async (templateId) => {
    const { onClose, onTemplateSelect } = this.props;
    this.setLoadingStatus(true);
    if (onTemplateSelect) {
      await onTemplateSelect({
        title: this.state.name,
        description: this.state.description,
        templateId,
      });
    }
    this.setLoadingStatus(false);
    if (onClose) {
      onClose();
    }
  };
  constructPathwayTypes = (contentProperties, defaultValue) => contentProperties.map((property) => ({
    key: property.path,
    value: defaultValue,
  }));

  skipPathwaySelection = async (pluginId, version, pluginType, pathwayTypes) => {
    const { onClose, onPluginSelect } = this.props;
    this.setLoadingStatus(true);
    if (onPluginSelect) {
      await onPluginSelect({
        title: this.state.name,
        description: this.state.description,
        pluginId,
        version,
        pluginType,
        pathwayTypes,
      });
    }
    this.setLoadingStatus(false);
    if (onClose) {
      onClose();
    }
  };

  handlePluginSelect = async (pluginId, version, pluginType, pluginName) => {
    const { pluginService } = this.props;
    const { COURSE, UNIT, LESSON, PATHWAY } = pluginTypes;

    if ([COURSE, UNIT, LESSON].includes(pluginType)) {
      this.setLoadingStatus(true);
      const plugin = await pluginService.getPlugin(pluginId, version);
      const pluginSchema = JSON.parse(plugin.manifest.configurationSchema);
      const contentProperties = findPropertiesByType(
        pluginSchema,
        PATHWAY,
      );
      if (contentProperties && contentProperties.length > 0) {
        if (pluginTypes.COURSE === pluginType && pluginsSkipChoosePathway.includes(pluginId)) {
          const pathwayTypes = this.constructPathwayTypes(contentProperties, pathwayTypesConstants.FREE);
          this.skipPathwaySelection(pluginId, version, pluginType, pathwayTypes);
        } else {
          this.goTo2ndStepPathways(pluginId, version, pluginType, pluginSchema);
        }
      } else {
        await this.confirmPluginSelect(pluginId, version, pluginType, pluginName);
      }
      if (!pluginsSkipChoosePathway.includes(pluginId)) {
        this.setLoadingStatus(false);
      }
    } else {
      await this.confirmPluginSelect(pluginId, version, pluginType, pluginName);
    }
  };

  confirmPluginSelect = async (pluginId, version, pluginType, pluginName) => {
    const { onClose, onPluginSelect } = this.props;
    this.setLoadingStatus(true);
    if (onPluginSelect) {
      await onPluginSelect({
        title: this.state.name,
        description: this.state.description,
        pluginId,
        version,
        pluginType,
        pluginName,
      });
    }
    this.setLoadingStatus(false);
    if (onClose) {
      onClose();
    }
  };

  confirmPathwayTypeSelect = async (pathwayTypes) => {
    const { onClose, onPluginSelect } = this.props;
    const { selectedActivity } = this.state;
    this.setLoadingStatus(true);
    if (onPluginSelect) {
      await onPluginSelect({
        title: this.state.name,
        description: this.state.description,
        pluginId: selectedActivity.pluginId,
        version: selectedActivity.version,
        pluginType: selectedActivity.pluginType,
        pathwayTypes,
      });
    }
    this.setLoadingStatus(false);
    if (onClose) {
      onClose();
    }
  };

  generateNavList = () => {
    const { templateService, pluginType, navItems, plugins } = this.props;
    const templateCategories = templateService.getTemplateCategories(pluginType);
    const templateNavItems = this.getTemplateNavItems(templateCategories);
    const pluginCategory = this.getPluginCategory(plugins);

    let menuItems = [];
    menuItems = Object.assign([], menuItems, templateNavItems);
    menuItems = Object.assign([], menuItems, navItems);
    menuItems = menuItems.concat(pluginCategory);

    if (!this.state.selectedCategory && menuItems.length) {
      this.setState({
        selectedCategory: menuItems[0].key,
        selectedView: menuItems[0].type,
      });
    }

    return menuItems;
  };

  render() {
    const { config, plugins, pluginType } = this.props;
    const { selectedCategory, name, description, selectedView, isLoading, selectedActivity } = this.state;

    const navItems = this.generateNavList();
    const showCategoryList = navItems && navItems.length > 0;

    const viewProps = {
      selectedCategory,
    };

    const view2ndStepProps = {
      selectedActivity,
    };

    let header2ndStepProps;
    if (selectedActivity) {
      const config2ndStep = getConfig2ndStep(selectedActivity.pluginType);
      header2ndStepProps = {
        title: config2ndStep.title,
        description: config2ndStep.description,
        descriptionValues: config2ndStep.descriptionValues,
      };
    } else {
      header2ndStepProps = {};
    }

    if (isLoading) {
      return <LoadingWrapper><Spinner /></LoadingWrapper>;
    }
    return (
      <FullHeightWrapper>
        <CenteringWrapper>
          {selectedView === constants.PLUGIN_2ND_STEP_PATHWAYS
            ? <Fragment>
              <PluginSelectionHeader2ndStep
                {...header2ndStepProps}
                onBack={this.goBackTo1stStep}
              />
              <PluginSelection2ndStepWrapper>
                <PluginSelection2ndStep
                  {...view2ndStepProps}
                  onPathwayTypeSelect={this.confirmPathwayTypeSelect}
                />
              </PluginSelection2ndStepWrapper>
            </Fragment>

            : <Fragment>
              <PluginSelectionHeader
                title={config.title}
                templateTitle={config.templateTitle}
                templateDescription={config.templateDescription}
                placeholder={config.placeholder}
                descriptionPlaceholder={config.descriptionPlaceholder}
                value={name}
                descriptionValue={description}
                showDescriptionInput={config.showDescriptionInput}
                onChange={this.updateInputField}
              />
              <PluginCollection>
                {showCategoryList &&
                  <CategoryList
                    selectedCategory={selectedCategory}
                    onSelectCategory={this.setSelectedCategory}
                    navItems={navItems}
                  />
                }

                {selectedView === constants.PLUGIN &&
                  <PluginSelectionView
                    {...viewProps}
                    plugins={plugins}
                    onPluginSelect={this.handlePluginSelect}
                  />
                }
                {selectedView === constants.TEMPLATE &&
                  <TemplateSelectionView
                    {...viewProps}
                    pluginType={pluginType}
                    onTemplateSelect={this.handleTemplateSelect}
                  />
                }
                {(selectedView !== constants.PLUGIN && selectedView !== constants.TEMPLATE) && <div />}
              </PluginCollection>
            </Fragment>
          }
        </CenteringWrapper>
      </FullHeightWrapper>
    );
  }
}

PluginSelectionModal.propTypes = {
  config: PropTypes.object,
  navItems: PropTypes.array,
  plugins: PropTypes.array,
  pluginType: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  getPlugins: PropTypes.func,
  onClose: PropTypes.func,
  onPluginSelect: PropTypes.func,
  onTemplateSelect: PropTypes.func,
  templateService: templateServiceShape,
  pluginService: pluginServiceShape,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getPlugins: () => {
    if (Array.isArray(ownProps.pluginType)) {
      ownProps.pluginType.forEach((pluginType) => {
        dispatch(commandAuthorPluginList({ pluginType }));
      });
    } else if (Array.isArray(ownProps.pluginTypes)) {
      ownProps.pluginTypes.forEach((pluginType) => {
        dispatch(commandAuthorPluginList({ pluginType }));
      });
    } else {
      dispatch(commandAuthorPluginList({ pluginType: ownProps.pluginType }));
    }
  },
});

const mapStateToProps = (state, ownProps) => {
  let plugins = [];

  if (Array.isArray(ownProps.pluginType)) {
    ownProps.pluginType.forEach((pluginType) => {
      plugins = plugins.concat(selectPluginTypeList(state, pluginType) || []);
    });
  } else if (Array.isArray(ownProps.pluginTypes)) {
    ownProps.pluginTypes.forEach((pluginType) => {
      plugins = plugins.concat(selectPluginTypeList(state, pluginType) || []);
    });
  } else {
    plugins = selectPluginTypeList(state, ownProps.pluginType);
  }

  return {
    plugins,
  };
};

const ThemedPluginSelectionModal = enhanceWithThemes(PluginSelectionModal, themes);

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const ComposedComponent = compose(
  enhanceWithServices,
  withConnect,
)(ThemedPluginSelectionModal);

ComposedComponent.getConfig = getConfig;

export default ComposedComponent;
